/**
 * dateGroup — label a Unix-second timestamp as "Today" / "Yesterday" /
 * "May 19" (current year) / "May 19, 2024" (older year). Localtime.
 *
 * Used by ChatView to insert subtle day dividers between message
 * bubbles whenever the day boundary changes between adjacent msgs.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Day key — same value for every msg sent on the same localtime day.
 * Used to compare adjacent bubbles for boundary detection.
 */
export function dayKey(tsSec: string | number): string {
  const ts = typeof tsSec === "number" ? tsSec : Number.parseInt(tsSec, 10);
  if (!Number.isFinite(ts)) return "";
  const d = new Date(ts * 1000);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Human label for a divider row. */
export function dayLabel(tsSec: string | number, now: Date = new Date()): string {
  const ts = typeof tsSec === "number" ? tsSec : Number.parseInt(tsSec, 10);
  if (!Number.isFinite(ts)) return "";
  const d = new Date(ts * 1000);
  const today = startOfDay(now);
  const that = startOfDay(d);
  const dayMs = 86_400_000;
  if (that === today) return "Today";
  if (that === today - dayMs) return "Yesterday";
  const sameYear = d.getFullYear() === now.getFullYear();
  const m = MONTHS[d.getMonth()] ?? "";
  if (sameYear) return `${m} ${d.getDate()}`;
  return `${m} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Build a centered, muted divider element for a given timestamp. */
export function buildDayDivider(tsSec: string | number): HTMLElement {
  const row = document.createElement("div");
  row.className = "chat-view__divider";
  const lbl = document.createElement("span");
  lbl.className = "chat-view__divider-lbl";
  lbl.textContent = dayLabel(tsSec);
  row.appendChild(lbl);
  return row;
}
